// apps/support-console/src/api.ts
import type {
  AgentUpdateTicketInput,
  PostMessageInput,
  SupportAgentStatus,
  SupportTicketDetailDTO,
  SupportTicketSummaryDTO,
} from '@ayiland/support-types';
import { config } from './config';
import { useAuthStore } from './store';

async function call<T>(
  url: string,
  init: RequestInit = {},
  bodyJson?: unknown,
): Promise<T> {
  const token = useAuthStore.getState().session?.token;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init.headers as Record<string, string> | undefined),
  };
  const res = await fetch(url, {
    ...init,
    headers,
    body: bodyJson !== undefined ? JSON.stringify(bodyJson) : init.body,
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean } & T;
  if (!res.ok || data.success === false) {
    throw new Error(
      ((data as { message?: string }).message) || `HTTP ${res.status}`,
    );
  }
  return data;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Auth (calls main @ayiland/api)
// ─────────────────────────────────────────────────────────────────────────────

export interface LoginResponse {
  user: {
    id: string;
    email: string;
    displayName: string;
    role: string;
  };
  accessToken: string;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  return call<LoginResponse>(
    `${config.apiUrl}/api/auth/login`,
    { method: 'POST' },
    { email, password },
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Agent endpoints (support-service)
// ─────────────────────────────────────────────────────────────────────────────

const SUP = `${config.supportUrl}/api/support/agent`;

export type QueueScope = 'queued' | 'mine' | 'active' | 'all';

export async function listQueue(scope: QueueScope = 'queued'): Promise<{ tickets: SupportTicketSummaryDTO[] }> {
  return call<{ tickets: SupportTicketSummaryDTO[] }>(
    `${SUP}/queue?scope=${scope}`,
  );
}

export async function getTicket(id: string): Promise<{ ticket: SupportTicketDetailDTO }> {
  return call<{ ticket: SupportTicketDetailDTO }>(`${SUP}/tickets/${id}`);
}

export async function claimTicket(id: string): Promise<{ ticket: SupportTicketDetailDTO }> {
  return call<{ ticket: SupportTicketDetailDTO }>(
    `${SUP}/tickets/${id}/claim`,
    { method: 'POST' },
    {},
  );
}

export async function postAgentMessage(
  ticketId: string,
  body: PostMessageInput,
): Promise<{ message: unknown }> {
  return call(`${SUP}/tickets/${ticketId}/messages`, { method: 'POST' }, body);
}

export async function patchTicket(
  ticketId: string,
  patch: AgentUpdateTicketInput,
): Promise<{ ticket: SupportTicketDetailDTO }> {
  return call<{ ticket: SupportTicketDetailDTO }>(
    `${SUP}/tickets/${ticketId}`,
    { method: 'PATCH' },
    patch,
  );
}

export async function transferTo(
  ticketId: string,
  toAgentId: string,
): Promise<{ ticket: SupportTicketDetailDTO }> {
  return call(`${SUP}/tickets/${ticketId}/transfer`, { method: 'POST' }, { toAgentId });
}

export async function setPresence(status: SupportAgentStatus): Promise<void> {
  await call(`${SUP}/presence`, { method: 'POST' }, { status });
}
